import context from "core/tv-guide.context";
import { ChannelService } from "core/services/channel.service";
import { PopupTemplate, PopupTemplateData } from "./popup-template";
import TvGuideStore from "core/providers/tv-guide.store";
import { Channel } from "core/types";

export interface ChannelPopupConfiguration {
  parentClassName: string;
  targetClassName: string;
}

enum PopupAction {
  Open,
  Close
}

//TODO: Change the way this works. It's convoluted.

const ChannelPopupCss = {
  parent: "channel-popup-parent",
  target: "channel-popup-target",
  removeScrolling: "remove-scrolling"
}

export class ChannelPopup {
  private readonly _store: TvGuideStore;
  private template: any = null;
  private _rootElementId = "channel-popup-root";
  private parentClassName = ChannelPopupCss.parent;
  private targetClassName = ChannelPopupCss.target;
  private targetParentRef: any = null;
  // eslint-disable-next-line @typescript-eslint/ban-types
  private eventListeners: { eventElementRef: any, eventName: string, eventHandlerRef: Function, action: PopupAction }[] = [];

  constructor(store: TvGuideStore, config?: ChannelPopupConfiguration) {
    this._store = store;
    this.init(config);
  }

  init(config?: ChannelPopupConfiguration) {
    this.parentClassName = config?.parentClassName || ChannelPopupCss.parent;
    this.targetClassName = config?.targetClassName || ChannelPopupCss.target;

    this.addOpenListener();
    // this.intersector = new IntersectionObserverWrapper();
  }

  static Create(store: TvGuideStore, config?: ChannelPopupConfiguration) {
    return new ChannelPopup(store, config);
  }

  get rootElement() {
    return document.getElementById(this._rootElementId) as HTMLElement;
  }

  getTargetParentElement() {
    return document.getElementById(this.parentClassName)
  }

  addOpenListener() {
    if (this.targetParentRef) {
      return;
    }

    this.targetParentRef = this.getTargetParentElement();

    if (!this.targetParentRef) {
      console.error(`Target element of '${this.parentClassName}' was not found`);
      return;
    }

    if (this.targetParentRef.getAttribute("channel-popup-external-event") !== "true") {
      this.targetParentRef.addEventListener("click", this.openPopup);

      this.eventListeners.push({
        eventElementRef: this.targetParentRef,
        eventName: "click",
        eventHandlerRef: this.openPopup,
        action: PopupAction.Open
      });
    }
  }

  addCloseListeners() {
    const closeButton = document.getElementsByClassName("channel-popup-header-close");
    const overlay = document.getElementsByClassName("channel-popup-overlay");

    document.addEventListener("keyup", this.escapeClose);
    this.eventListeners.push({
      eventElementRef: document,
      eventName: "keyup",
      eventHandlerRef: this.escapeClose,
      action: PopupAction.Close
    });

    [...Array.from(closeButton), ...Array.from(overlay)]
      .forEach((element: any) => {
        element.addEventListener("click", this.closePopup);
        this.eventListeners.push({
          eventElementRef: element,
          eventName: "click",
          eventHandlerRef: this.closePopup,
          action: PopupAction.Close
        });
      });
  }

  protected escapeClose = (ev: KeyboardEvent) => {
    if (ev?.key === "Escape") {
      return this.closePopup();
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => { };
  }

  protected openPopup: (event?: Event, element?: HTMLElement) => void = (event?: Event, element?: HTMLElement) => {
    this.targetParentRef?.setAttribute("channel-popup-external-event", "true");

    if (document.getElementById(this._rootElementId)) {
      return;
    }

    const clickedElement = event?.target as HTMLElement;
    const target = clickedElement.className.split(" ").indexOf(this.targetClassName) >= 0
      ? clickedElement
      : clickedElement.closest(`.${this.targetClassName}`)

    let channelTag: any;
    let channelNumber: any;

    if (context.useChannelTag) {
      channelTag = target?.getAttribute("data-cdp-channel-tag");

      if (!channelTag) {
        console.error(`Invalid Channel Tag of ${channelTag}`)
        return;
      }

    } else {
      channelNumber = target?.getAttribute("data-cdp-channel-number");

      if (!channelNumber) {
        console.error(`Invalid Channel Number of ${channelNumber}`)
        return;
      }
    }

    this._initializeElement();

    if (this._populateTemplateFromStore(channelNumber, channelTag)) {
      return;
    }

    //shouldn't be needed
    this._populateTemplateFromService(channelNumber, channelTag);
  }

  private _populateTemplateFromService(channelNumber: any, channelTag: any) {
    const service = new ChannelService();

    service.getChannnel(channelTag, channelNumber)
      .then(res => {
        if (!res.data) {
          console.error(`Error fetching channel details for channel - ${channelTag}`);
          return;
        }

        this.destroy();
        // this.intersector.disconnect();

        const channel = res.data;

        this._populateTemplateFromChannel(channel);
      });
  }

  private _populateTemplateFromStore(channelNumber: any, channelTag: any) {
    let channel: Channel | undefined = undefined;

    const store = this._store;

    if (channelNumber) {
      channel = store.channels[channelNumber];
    }

    if (channelTag) {
      const match = Object.values(store.channels).find((program) => program.tag === channelTag);
      if (match) {
        channel = match;
      }
    }

    return this._populateTemplateFromChannel(channel);
  }

  private _populateTemplateFromChannel(channel: Channel | undefined) {
    if (!channel) {
      return false;
    }

    const data: PopupTemplateData = {
      channelNumber: channel.number.toString(),
      channelImageSrc: channel.logo,
      header: channel.name,
      bodyText: channel.channelDetail,
      buttons: {
        primary: { url: channel.channelWebsiteLink, text: "Visit Website" },
        secondary: { url: channel.dStvNowLink, text: "Watch Now" },
        tertiary: { url: channel.votingLink, text: "Vote" }
      },
      bannerImageSrc: channel.thumbnailImageUrl
    };

    this.template = PopupTemplate(false, data);

    const el = this.rootElement;
    el.innerHTML = `${this.template}`;

    this.addCloseListeners();
    // this.intersector.createObserver('.channel-popup-main');

    return true;
  }

  private _initializeElement() {
    const el = this.rootElement;

    if (el) {
      return;
    }

    this.template = PopupTemplate(true);
    document.body.insertAdjacentHTML("beforeend", `<div id="${this._rootElementId}">${this.template}</div>`);
    document.body.classList.add(ChannelPopupCss.removeScrolling)
  }

  protected closePopup: (event?: Event, element?: HTMLElement) => void = (event, element) => {
    this.targetParentRef?.setAttribute("channel-popup-external-event", "false");

    const el = this.rootElement;

    if (el) {
      document.body.removeChild(el);
      document.body.classList.remove(ChannelPopupCss.removeScrolling);
    }
    // this.intersector.disconnect();
    this.destroy();
  }

  private destroy(onlyDestroyClose = true) {
    this.eventListeners
      .filter(event => !onlyDestroyClose || event.action === PopupAction.Close)
      .forEach(event => {
        if (event.eventElementRef) {
          event.eventElementRef.removeEventListener(event.eventName, event.eventHandlerRef);
        }
      })
  }
}
